import { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, Download, Search, ChevronRight, ShieldOff, ShieldCheck, Trash2 } from "lucide-react";

const PLANS = [
  { v: "", l: "Tous les plans" },
  { v: "free", l: "Free" },
  { v: "surveillance", l: "Surveillance" },
];

export default function AdminClients() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState(params.get("q") || "");
  const [plan, setPlan] = useState(params.get("plan") || "");
  const [disabled, setDisabled] = useState(params.get("disabled") || "");
  const [page, setPage] = useState(parseInt(params.get("page") || "1"));
  const per_page = 25;
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);

  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => {
    const ids = data.items.map(u => u.id);
    if (ids.every(id => selected.has(id))) setSelected(new Set());
    else setSelected(new Set([...selected, ...ids]));
  };

  const runBulkDelete = async () => {
    if (bulkConfirm !== "DELETE_CLIENTS_CONFIRM") return toast.error("Tapez exactement DELETE_CLIENTS_CONFIRM");
    setBulkRunning(true);
    try {
      const { data: res } = await adminApi.post("/clients/bulk-delete", {
        user_ids: [...selected], confirm: bulkConfirm,
      });
      toast.success(`${res.deleted} client(s) supprimé(s)`);
      setSelected(new Set()); setBulkOpen(false); setBulkConfirm(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBulkRunning(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: String(page), per_page: String(per_page) });
      if (q) p.set("q", q);
      if (plan) p.set("plan", plan);
      if (disabled !== "") p.set("disabled", disabled);
      const { data } = await adminApi.get(`/clients?${p}`);
      setData(data);
    } catch { toast.error("Erreur"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, plan, disabled]);

  const exportCsv = async () => {
    const token = localStorage.getItem("empco_admin_token");
    const url = `${adminApi.defaults.baseURL}/clients/export.csv${q ? `?q=${encodeURIComponent(q)}` : ""}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `empco-clients.csv`;
    a.click();
  };

  const totalPages = Math.max(1, Math.ceil(data.total / per_page));

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">Base clients</p>
          <h1 className="text-3xl font-semibold text-white flex items-center gap-3"><Users className="w-7 h-7 text-emerald-400" /> Clients</h1>
          <p className="text-sm text-slate-400 mt-1">{data.total.toLocaleString("fr-FR")} client(s) — hors comptes admin.</p>
        </div>
        <div className="flex items-center gap-3">
          {selected.size > 0 && (
            <button onClick={() => setBulkOpen(true)} data-testid="clients-bulk-delete-open"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-rose-600/20 border border-rose-500/40 text-rose-300 hover:bg-rose-600/30 text-sm">
              <Trash2 className="w-4 h-4" /> Supprimer ({selected.size})
            </button>
          )}
          <button onClick={exportCsv} data-testid="clients-export-csv" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/30 text-sm">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {bulkOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="bulk-delete-modal">
          <div className="bg-[#0f172a] border border-rose-500/40 rounded-xl p-6 max-w-lg w-full">
            <h3 className="text-lg text-rose-300 flex items-center gap-2 mb-3"><Trash2 className="w-5 h-5" /> Supprimer {selected.size} client(s) ?</h3>
            <p className="text-xs text-slate-400 mb-4 leading-relaxed">
              Cette action supprime définitivement les comptes sélectionnés + toutes leurs données (scans, commandes, factures, alertes, notes, journaux, sites, preuves, jetons). Les comptes admin ne sont jamais touchés.
            </p>
            <ul className="text-xs text-slate-300 mb-4 max-h-32 overflow-auto space-y-1 font-mono">
              {data.items.filter(u => selected.has(u.id)).map(u => (
                <li key={u.id}>— {u.email}{u.company ? ` (${u.company})` : ""}</li>
              ))}
            </ul>
            <input type="text" value={bulkConfirm} onChange={e => setBulkConfirm(e.target.value)}
              placeholder="Tapez DELETE_CLIENTS_CONFIRM"
              data-testid="bulk-delete-confirm"
              className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs font-mono mb-4" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setBulkOpen(false); setBulkConfirm(""); }} data-testid="bulk-delete-cancel"
                className="px-4 py-2 rounded-md border border-slate-700 text-slate-300 text-xs">Annuler</button>
              <button onClick={runBulkDelete} disabled={bulkRunning || bulkConfirm !== "DELETE_CLIENTS_CONFIRM"}
                data-testid="bulk-delete-run"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white text-xs">
                <Trash2 className="w-3.5 h-3.5" /> {bulkRunning ? "Suppression…" : "Confirmer la suppression"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-4">
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
              placeholder="Recherche email, entreprise, TVA…" data-testid="clients-search"
              className="w-full bg-[#0b1220] border border-slate-700 rounded-md pl-8 pr-3 py-2 text-xs" />
          </div>
          <select value={plan} onChange={(e) => { setPlan(e.target.value); setPage(1); }} data-testid="clients-plan-filter"
            className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs">
            {PLANS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
          <select value={disabled} onChange={(e) => { setDisabled(e.target.value); setPage(1); }} data-testid="clients-disabled-filter"
            className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs">
            <option value="">Tous statuts</option>
            <option value="false">Actifs</option>
            <option value="true">Suspendus</option>
          </select>
          <button onClick={() => { setPage(1); load(); }} data-testid="clients-search-btn"
            className="px-4 py-2 rounded-md bg-[#1F5A99] text-white text-xs">Filtrer</button>
        </div>

        <div className="overflow-auto max-h-[70vh] border border-slate-800 rounded-md">
          <table className="w-full text-xs">
            <thead className="bg-[#0b1220] sticky top-0">
              <tr className="text-left uppercase tracking-widest text-[10px] text-slate-500">
                <th className="p-3 w-8">
                  <input type="checkbox" onChange={toggleAll}
                    checked={data.items.length > 0 && data.items.every(u => selected.has(u.id))}
                    data-testid="bulk-toggle-all"
                    className="accent-rose-500 cursor-pointer" />
                </th>
                <th className="p-3">E-mail</th><th className="p-3">Entreprise</th><th className="p-3">Plan</th>
                <th className="p-3">Scans</th><th className="p-3">CA</th><th className="p-3">MRR</th>
                <th className="p-3">Statut</th><th className="p-3">Inscrit</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(u => (
                <tr key={u.id} className={`border-t border-slate-800 hover:bg-slate-800/30 ${selected.has(u.id) ? "bg-rose-500/5" : ""}`} data-testid={`client-row-${u.id.slice(0,8)}`}>
                  <td className="p-3">
                    <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)}
                      data-testid={`bulk-check-${u.id.slice(0,8)}`}
                      className="accent-rose-500 cursor-pointer" />
                  </td>
                  <td className="p-3 text-slate-200">{u.email}</td>
                  <td className="p-3 text-slate-400">{u.company || "—"}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase ${u.plan === "surveillance" ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-800 text-slate-400"}`}>{u.plan || "free"}</span>
                  </td>
                  <td className="p-3 font-mono">{u.scans_count}</td>
                  <td className="p-3 font-mono text-emerald-400">{(u.orders_count || 0)}</td>
                  <td className="p-3 font-mono">{u.mrr_eur ? `${u.mrr_eur} €` : "—"}</td>
                  <td className="p-3">
                    {u.disabled
                      ? <span className="inline-flex items-center gap-1 text-rose-400"><ShieldOff className="w-3 h-3" /> Suspendu</span>
                      : <span className="inline-flex items-center gap-1 text-emerald-400"><ShieldCheck className="w-3 h-3" /> Actif</span>}
                  </td>
                  <td className="p-3 text-slate-500 font-mono">{u.created_at?.slice(0, 10)}</td>
                  <td className="p-3">
                    <Link to={`/admin/clients/${u.id}`} data-testid={`client-view-${u.id.slice(0,8)}`}
                      className="inline-flex items-center gap-1 text-emerald-400 hover:underline text-xs">Fiche <ChevronRight className="w-3 h-3" /></Link>
                  </td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-slate-500">Aucun client</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4 text-xs">
          <span className="text-slate-500">Page {page}/{totalPages} — {data.total} client(s)</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} data-testid="clients-prev"
              className="px-3 py-1.5 rounded-md border border-slate-800 disabled:opacity-40">Précédent</button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="clients-next"
              className="px-3 py-1.5 rounded-md border border-slate-800 disabled:opacity-40">Suivant</button>
          </div>
        </div>
      </div>
    </div>
  );
}
